import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import './Partner.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const PRODUCTS = [
  { value: 'bltz_runtime', label: 'Bltz Runtime', path: '/runtime-shield', description: 'Real-time AI traffic monitoring and policy enforcement at the network layer' },
  { value: 'bltz_purple_team', label: 'Bltz Purple Team', path: '/purple-team', description: 'AI Agent Penetration Testing & Auto-Remediation' },
  { value: 'bltz_workspace', label: 'Bltz Workspace', path: '/ai-governance', description: 'AI Governance, Visibility, Policy Enforcement, Risk Monitoring for AI Agents in Endpoints, Browser, Code, MCP, and others' },
];

const DealRegistration = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = location.state?.user;

  const [form, setForm] = useState({
    partner_rep_name: '',
    partner_rep_email: user?.email || '',
    prospect_company: '',
    prospect_contact_name: '',
    prospect_contact_email: '',
    use_case: '',
    products: [],
    estimated_opportunity_size: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleProductToggle = (value) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.includes(value)
        ? prev.products.filter((p) => p !== value)
        : [...prev.products, value],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.partner_rep_name || !form.partner_rep_email || !form.prospect_company) {
      setError('Please fill in all required fields');
      return;
    }
    if (form.products.length === 0) {
      setError('Please select at least one product');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/partner-deals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, submitted_by_email: user?.email || '' }),
      });
      const data = await res.json();

      if (!data.success) {
        setError(data.error || 'Failed to submit deal registration');
        setLoading(false);
        return;
      }

      setSuccess('Thank you for your submission! Partner team will be reaching out to you on this deal shortly.');
      setTimeout(() => navigate('/partner/dashboard', { state: { user, dealRegistered: true } }), 2000);
    } catch (err) {
      setError(err.message || 'Failed to submit');
    }
    setLoading(false);
  };

  return (
    <>
      <section className="main-hero demo-hero">
        <div className="hero-gif-fade-left"></div>
        <div className="hero-gif-fade-right"></div>
        <h1 className="hero-page-title">Deal Registration</h1>
      </section>

      <section className="ptn-content">
        <div className="ptn-login-container" style={{ maxWidth: 600 }}>
          {error && <div className="ptn-login-error">{error}</div>}
          {success && <div className="ptn-login-success">{success}</div>}

          <form className="ptn-login-form" onSubmit={handleSubmit}>
            <label className="ptn-login-label" htmlFor="dr-rep-name">Partner Rep Name *</label>
            <input
              className="ptn-login-input"
              id="dr-rep-name"
              name="partner_rep_name"
              value={form.partner_rep_name}
              onChange={handleChange}
              placeholder="Your full name"
              required
            />

            <label className="ptn-login-label" htmlFor="dr-rep-email">Partner Rep Email *</label>
            <input
              className="ptn-login-input"
              id="dr-rep-email"
              name="partner_rep_email"
              type="email"
              value={form.partner_rep_email}
              onChange={handleChange}
              placeholder="you@company.com"
              required
            />

            <label className="ptn-login-label" htmlFor="dr-prospect-company">Prospect Company *</label>
            <input
              className="ptn-login-input"
              id="dr-prospect-company"
              name="prospect_company"
              value={form.prospect_company}
              onChange={handleChange}
              placeholder="Company name"
              required
            />

            <label className="ptn-login-label" htmlFor="dr-prospect-name">Prospect Contact Name (Optional)</label>
            <input
              className="ptn-login-input"
              id="dr-prospect-name"
              name="prospect_contact_name"
              value={form.prospect_contact_name}
              onChange={handleChange}
              placeholder="Contact full name"
            />

            <label className="ptn-login-label" htmlFor="dr-prospect-email">Prospect Contact Email (Optional)</label>
            <input
              className="ptn-login-input"
              id="dr-prospect-email"
              name="prospect_contact_email"
              type="email"
              value={form.prospect_contact_email}
              onChange={handleChange}
              placeholder="contact@company.com"
            />

            <label className="ptn-login-label" htmlFor="dr-use-case">Background</label>
            <textarea
              className="ptn-login-input ptn-textarea"
              id="dr-use-case"
              name="use_case"
              value={form.use_case}
              onChange={handleChange}
              placeholder="Describe the prospect customer's background, use case or pain point"
              rows={4}
            />

            <label className="ptn-login-label">Product *</label>
            <div className="ptn-checkbox-group">
              {PRODUCTS.map((p) => (
                <label key={p.value} className="ptn-checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.products.includes(p.value)}
                    onChange={() => handleProductToggle(p.value)}
                  />
                  <span className="ptn-checkbox-text">
                    <Link
                      to={p.path}
                      className="ptn-checkbox-product-name"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >{p.label}</Link>
                    <span className="ptn-checkbox-product-desc">{p.description}</span>
                  </span>
                </label>
              ))}
            </div>

            <label className="ptn-login-label" htmlFor="dr-opp-size">Estimated Opportunity Size</label>
            <input
              className="ptn-login-input"
              id="dr-opp-size"
              name="estimated_opportunity_size"
              value={form.estimated_opportunity_size}
              onChange={handleChange}
              placeholder="e.g. $50,000"
            />

            <button className="ptn-login-submit" type="submit" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Deal Registration'}
            </button>
          </form>

          <p className="ptn-login-switch">
            <button
              className="ptn-login-switch-btn"
              onClick={() => navigate('/partner/dashboard', { state: { user } })}
            >
              ← Back to Dashboard
            </button>
          </p>
        </div>
      </section>
    </>
  );
};

export default DealRegistration;
